"use client";

import { ChevronDown } from "lucide-react";
import type { CareersHeroComponent } from "../../types";
import { RuntimeImage } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";

/** French typography: high punctuation (: ; ! ?) must never start a line. */
function frenchNbsp(text: string): string {
  return text.replace(/ ([:;!?»])/g, " $1");
}

/**
 * Bandeau héros recrutement (maquette designer) : panneau arrondi à la largeur
 * du contenu, fond duotone bleu du siège BCC (la bande blanche du bas de
 * l'asset est rognée par object-cover ancré en haut), photo détourée du couple
 * ancrée bas-droite, texte à gauche (titre 2 tons en capitales, paragraphe,
 * accroche) et CTA ambre en ANCRE vers une section de la même page.
 *
 * Hauteur volontairement LIMITÉE : ~30rem desktop, ~24rem tablette ; sur
 * mobile le texte prend toute la largeur et la photo, réduite, reste ancrée
 * en bas à droite — texte et couple gardent leur place à chaque taille.
 */
export function CareersHeroRenderer({ component }: { component: CareersHeroComponent }) {
  const { title, titleAccent, body, tagline, ctaLabel, ctaTargetId, backgroundImage, foregroundImage, foregroundImageAlt } =
    component.content;
  const bg = resolveImageValue(backgroundImage);
  const fg = resolveImageValue(foregroundImage);

  return (
    /* Wrapper SANS overflow : le panneau arrondi vit dedans, et le couple —
       frère du panneau — peut déborder sous son bord bas (la main « sort » du
       panneau, comme sur la maquette). Le pb réserve la place du débord. */
    /* bcc-article-hero : sous lg, le CSS hôte colle le panneau aux bords de
       l'écran ET au bandeau sous l'en-tête (gauche/droite/haut) — desktop garde
       la carte arrondie en retrait. Même mécanisme que le héros d'article. */
    <div className="bcc-article-hero relative pb-6">
      <section className="relative overflow-hidden rounded-none bg-[#0B2B66] text-white ring-1 ring-black/10 lg:rounded-md">
        {bg.src ? (
          <RuntimeImage
            src={bg.src}
            alt=""
            aria-hidden="true"
            fill
            sizes="(max-width: 1200px) 100vw, 1200px"
            /* Cover cadré sur l'arche du siège (60% 35%) : centré ou ancré en haut,
               les panneaux hauts/étroits ne montraient que du ciel uni et le fond
               semblait « ne pas couvrir ». */
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: "60% 35%" }}
          />
        ) : null}
        {/* Voile de lisibilité : renforce le côté gauche où vit le texte. */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#04173a]/85 via-[#04173a]/35 to-[#04173a]/10" aria-hidden="true" />
        {/* Fondu bas : ancre le panneau dans le bleu profond quelle que soit la
            zone de la photo que le cover recadre (l'arche claire coupée net au
            bord bas rendait le pied du panneau brouillon). */}
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#0A2A64] to-transparent" aria-hidden="true" />

        {/* Hauteur JAMAIS fixe : min-h seulement — le texte allonge le panneau au
            lieu d'en déborder. Le pb mobile réserve la bande de la photo. */}
        <div className="relative z-10 flex flex-col justify-center px-6 pb-24 pt-10 sm:px-8 md:min-h-[22rem] md:max-w-[58%] md:pb-12 md:pt-10 lg:min-h-[26rem] lg:max-w-[55%] lg:px-12 lg:py-12">
        {title || titleAccent ? (
          <h2
            className="text-2xl font-bold uppercase leading-tight tracking-wide sm:text-3xl lg:text-4xl"
            style={{ fontFamily: "var(--font-aleo)", textWrap: "balance" }}
          >
            {title ? <span className="block text-white">{frenchNbsp(title)}</span> : null}
            {titleAccent ? <span className="block text-[#6FC3F0]">{frenchNbsp(titleAccent)}</span> : null}
          </h2>
        ) : null}
        {body ? (
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/90 sm:text-[15px] lg:text-base" style={{ fontFamily: "Nunito Sans" }}>
            {frenchNbsp(body)}
          </p>
        ) : null}
        {tagline ? (
          <p className="mt-4 text-base font-bold text-white sm:text-lg" style={{ fontFamily: "var(--font-aleo)" }}>
            {frenchNbsp(tagline)}
          </p>
        ) : null}
        {ctaLabel && ctaTargetId ? (
          <div className="mt-6">
            <a
              href={`#${ctaTargetId}`}
              className="inline-flex items-center gap-2 rounded-full bg-[#FFA401] px-6 py-2.5 text-sm font-semibold text-white transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 sm:text-base"
            >
              {ctaLabel}
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        ) : null}
        </div>
      </section>

      {/* Photo détourée : HORS du panneau (frère), ancrée bas-droite du wrapper —
          la main et le portable débordent sous le bord du panneau (design). */}
      {fg.src ? (
        <>
          {/* right-0 : la tranche du portable (bord coupé de l'asset) s'aligne
              EXACTEMENT sur le bord droit du panneau — un retrait laisserait
              flotter la coupe sur le bleu. */}
          <RuntimeImage
            src={fg.src}
            alt={foregroundImageAlt ?? ""}
            width={1200}
            height={825}
            /* Hauteur en % du wrapper : la photo suit la taille réelle du panneau
               (qui n'est plus figée) au lieu d'une hauteur codée en dur. */
            className="pointer-events-none absolute bottom-0 right-0 z-[2] hidden w-auto max-w-[55%] object-contain object-right-bottom md:block md:h-[88%] lg:h-[92%]"
          />
          {/* Variante mobile : bande photo à droite. CALAGE VALIDÉ : débord de 12px
              sous le panneau (wrapper pb-6 = 24px de réserve, image à bottom-3 =
              12px au-dessus du bas du wrapper). Ne changer QUE bottom-* ici pour
              retoucher le mobile — la réserve pb du wrapper est partagée avec le
              desktop (calé à 24px de débord, image à bottom-0). */}
          <RuntimeImage
            src={fg.src}
            alt=""
            aria-hidden="true"
            width={700}
            height={481}
            className="pointer-events-none absolute bottom-3 right-0 z-[2] block h-44 max-h-[52%] w-auto max-w-[72%] object-contain object-right-bottom md:hidden"
          />
        </>
      ) : null}
    </div>
  );
}
